/**
 * Session Verify Page
 * ===================
 * Simplified verification flow using session URL.
 *
 * Access via: /v/{token}
 *
 * This page validates the session token, gets an SDK access token,
 * then renders the VerificationSDK component.
 *
 * This is the simpler flow (compared to SDKVerifyPage):
 * - URL contains only the session token (no query params needed)
 * - Token validation happens automatically
 * - SDK access token is obtained server-side
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VerificationSDK from './VerificationSDK';

export default function SessionVerifyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine API base URL from current environment
  const apiBaseUrl = useMemo(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    // Handle case where apiBase already includes /api/v1
    if (apiBase.endsWith('/api/v1')) {
      return apiBase;
    }
    // Remove trailing slash if present
    const cleanBase = apiBase.replace(/\/$/, '');
    return `${cleanBase}/api/v1`;
  }, []);

  const sdkBaseUrl = useMemo(() => {
    return `${apiBaseUrl}/sdk`;
  }, [apiBaseUrl]);

  // Validate session token on mount
  useEffect(() => {
    const validateSession = async () => {
      if (!token) {
        setError('No session token provided');
        setLoading(false);
        return;
      }

      // If token is "new", create a fresh session and redirect
      if (token === 'new') {
        try {
          const createResponse = await fetch(`${apiBaseUrl}/verify/create-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ label: 'public' }),
          });

          if (!createResponse.ok) {
            setError('Failed to create verification session.');
            setLoading(false);
            return;
          }

          const createData = await createResponse.json();
          // Replace URL so refresh doesn't create another session
          navigate(`/v/${createData.token}`, { replace: true });
          return;
        } catch (err) {
          console.error('Session creation error:', err);
          setError('Failed to connect to verification service.');
          setLoading(false);
          return;
        }
      }

      try {
        const response = await fetch(`${apiBaseUrl}/sdk/session/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('This verification link has expired or is invalid.');
          } else if (response.status === 400) {
            const data = await response.json();
            setError(data.detail || 'This session is no longer active.');
          } else {
            setError('Failed to load verification session.');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setSessionData(data);
        setLoading(false);
      } catch (err) {
        console.error('Session validation error:', err);
        setError('Failed to connect to verification service.');
        setLoading(false);
      }
    };

    validateSession();
  }, [token, apiBaseUrl, navigate]);

  const handleComplete = (result) => {
    console.log('Verification complete:', result);

    // If there's a redirect URL from the session, redirect there
    if (sessionData?.redirect_url) {
      const separator = sessionData.redirect_url.includes('?') ? '&' : '?';
      window.location.href = `${sessionData.redirect_url}${separator}status=${result?.status || 'completed'}&applicant_id=${sessionData.applicant_id}`;
      return;
    }

    // Post message to parent window (for iframe integration)
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'getclearance:complete', result },
        '*'
      );
    }
  };

  const handleError = (error) => {
    console.error('Verification error:', error);

    // Post message to parent window
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'getclearance:error', error: error.message },
        '*'
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="session-page">
        <div className="session-loading">
          <div className="spinner" />
          <p>Loading verification...</p>
        </div>
        <style>{sessionStyles}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="session-page">
        <div className="session-error">
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1>Unable to Load Verification</h1>
          <p>{error}</p>
          <p className="hint">Please request a new verification link from the application.</p>
        </div>
        <style>{sessionStyles}</style>
      </div>
    );
  }

  // Session not active
  if (!sessionData?.is_active) {
    return (
      <div className="session-page">
        <div className="session-error">
          <div className="error-icon warning">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1>Session {sessionData?.status === 'completed' ? 'Completed' : 'Expired'}</h1>
          <p>
            {sessionData?.status === 'completed'
              ? 'This verification has already been completed.'
              : 'This verification link is no longer active.'}
          </p>
          <p className="hint">Please request a new verification link if needed.</p>
        </div>
        <style>{sessionStyles}</style>
      </div>
    );
  }

  // Render SDK with the access token from session validation
  return (
    <div className="session-page">
      <VerificationSDK
        accessToken={sessionData.access_token}
        baseUrl={sdkBaseUrl}
        onComplete={handleComplete}
        onError={handleError}
        theme="light"
      />
      <style>{sessionStyles}</style>
    </div>
  );
}

const sessionStyles = `
  .session-page {
    --primary: #0D9488;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .session-loading {
    text-align: center;
    padding: 48px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .session-loading p {
    color: #6b7280;
    margin: 0;
  }

  .session-error {
    text-align: center;
    padding: 48px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
    max-width: 400px;
  }

  .error-icon {
    color: #ef4444;
    margin-bottom: 16px;
  }

  .error-icon.warning {
    color: #f59e0b;
  }

  .session-error h1 {
    color: #1f2937;
    font-size: 20px;
    margin: 0 0 12px;
  }

  .session-error p {
    color: #6b7280;
    margin: 0 0 8px;
  }

  .session-error .hint {
    font-size: 14px;
    color: #9ca3af;
  }

  @media (max-width: 480px) {
    .session-page {
      padding: 0;
      align-items: stretch;
    }
    .session-error,
    .session-loading {
      border-radius: 0;
      box-shadow: none;
      max-width: none;
    }
  }
`;
